import { useState, useRef } from "react";
import { useMutation } from "@apollo/client";
import {
  FiDownload,
  FiFileText,
  FiImage,
  FiEdit,
  FiUpload,
} from "react-icons/fi";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { Switcher } from "components/base/Form";
import { CREATE_CATALOG, UPDATE_CATALOG } from "./graphql";

const CatalogFormModal = ({ cat = null, refetch, hide }) => {
  const isEdit = !!cat;
  const [createCatalog] = useMutation(CREATE_CATALOG);
  const [updateCatalog] = useMutation(UPDATE_CATALOG);
  const pdfInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: cat?.name || "",
    status: cat?.status?.toLowerCase() || "new",
    isActive: cat?.isActive ?? true,
    order: cat?.order || 0,
  });

  const [posterFile, setPosterFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(cat?.poster || null);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (type === "poster") {
      setPosterFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPosterPreview(reader.result);
      reader.readAsDataURL(file);
    } else if (type === "pdf") {
      setPdfFile(file);
    }
  };

  const handleDownload = async () => {
    if (!cat?.pdf) return;
    try {
      setDownloading(true);

      // Try fetching the file without credentials to avoid CORS preflight/credential issues
      const res = await fetch(cat.pdf, { credentials: "omit" });

      // If fetch fails or returns non-OK, fallback to opening in a new tab
      if (!res.ok) {
        window.open(cat.pdf, "_blank", "noopener,noreferrer");
        setDownloading(false);
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${cat.name || "catalog"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloading(false);
    } catch (error) {
      // Likely CORS/network error — show message and open in new tab as fallback
      Alert("error", error.message || "Download failed");
      try {
        window.open(cat.pdf, "_blank", "noopener,noreferrer");
      } catch (e) {
        // ignore
      }
      setDownloading(false);
    }
  };

  const handleSubmit = async () => {
    if (!isEdit && (!formData.name || !posterFile || !pdfFile)) {
      Alert("error", "Please fill in all required fields");
      return;
    }

    if (isEdit && !formData.name) {
      Alert("error", "Name is required");
      return;
    }

    try {
      setSubmitting(true);

      if (isEdit) {
        await updateCatalog({
          variables: {
            id: cat.id,
            name: formData.name,
            status: formData.status,
            isActive: formData.isActive,
            order: parseInt(formData.order),
            ...(posterFile && { poster: posterFile }),
            ...(pdfFile && { pdf: pdfFile }),
          },
        });
        Alert("success", "Catalog updated successfully");
      } else {
        await createCatalog({
          variables: {
            name: formData.name,
            status: formData.status,
            poster: posterFile,
            pdf: pdfFile,
          },
        });
        Alert("success", "Catalog created successfully");
      }

      refetch();
      hide();
      setSubmitting(false);
    } catch (error) {
      Alert("error", error.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div className="flex items-center space-x-3">
        <FiEdit className="text-blue-500 text-2xl" />
        <h2 className="text-2xl font-bold">
          {isEdit ? "Edit Catalog" : "Create New Catalog"}
        </h2>
      </div>

      {/* Poster Preview with Click to Upload */}
      <div className="relative overflow-hidden rounded-xl shadow-lg group">
        {posterPreview ? (
          <>
            <img
              src={posterPreview}
              alt="Poster preview"
              className="w-full h-96 object-cover"
            />
            <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer flex items-center justify-center">
              <div className="text-center text-white">
                <FiUpload className="mx-auto text-5xl mb-3" />
                <p className="text-lg font-semibold">Click to change poster</p>
                <p className="text-sm mt-1">PNG, JPG up to 10MB</p>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "poster")}
                className="hidden"
              />
            </label>
          </>
        ) : (
          <label className="block border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl h-96 cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all">
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <FiImage className="text-6xl mb-4" />
              <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                Upload Poster Image *
              </p>
              <p className="text-sm mt-2">PNG, JPG up to 10MB</p>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, "poster")}
              className="hidden"
            />
          </label>
        )}
      </div>

      {/* Form Fields */}
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Catalog Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter catalog name"
          />
        </div>

        {/* Status, Display Order & Active */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Status</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="new">NEW</option>
              <option value="old">OLD</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Display Order
            </label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) =>
                setFormData({ ...formData, order: e.target.value })
              }
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Active</label>
            <div className="flex items-center h-[52px] px-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800">
              <Switcher
                isOn={formData.isActive}
                onChange={() =>
                  setFormData({ ...formData, isActive: !formData.isActive })
                }
              />
              <span className="ml-3 text-sm font-medium">
                {formData.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        {/* PDF Section */}
        <div>
          <label className="block text-sm font-medium mb-2">
            PDF Document <span className="text-red-500">*</span>
          </label>

          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <FiFileText className="text-gray-400 text-xl flex-shrink-0" />

            <div className="flex-1 min-w-0">
              {isEdit && cat.pdf && !pdfFile ? (
                <a
                  href={cat.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 truncate block"
                  title={cat.pdf}
                >
                  {cat.pdf.split("/").pop()}
                </a>
              ) : pdfFile ? (
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate block">
                  {pdfFile.name}
                </span>
              ) : (
                <span className="text-sm text-gray-400">No file selected</span>
              )}
            </div>

            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf"
              onChange={(e) => handleFileChange(e, "pdf")}
              className="hidden"
            />
            <Button
              onClick={() => pdfInputRef.current?.click()}
              className="flex items-center"
              icon={<FiUpload />}
            >
              {pdfFile ? "Change" : isEdit ? "Replace" : "Upload"}
            </Button>

            {isEdit && cat.pdf && (
              <Button
                onClick={handleDownload}
                loading={downloading}
                disabled={downloading}
                className="flex items-center"
                icon={<FiDownload />}
              >
                Download
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-3 pt-4 border-t dark:border-gray-700">
        <Button
          primary
          loading={submitting}
          disabled={submitting}
          onClick={handleSubmit}
          className="flex-1 justify-center"
        >
          {isEdit ? "Update Catalog" : "Create Catalog"}
        </Button>
      </div>
    </div>
  );
};

export default CatalogFormModal;
